import {InputText} from '@/components/commons/input-text/InputText'
import {ContactPhoneField} from '@/components/commons/contact-phone-field/ContactPhoneField'
import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ChevronDownIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {Label} from '@/components/ui/label/Label'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {Text} from '@/components/ui/text/Text'
import {UserPersonalDataFormValidationSchema} from '@/features/users/types'
import {useTranslation} from '@/translations/i18n'
import {Contact} from '@/types'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {useState} from 'react'
import {useFieldArray, useFormContext} from 'react-hook-form'

export const UserAdditionalContactsForm = () => {
    const {t} = useTranslation()
    const [isMenuOpen, setMenuOpen] = useState(false)

    const form = useFormContext<UserPersonalDataFormValidationSchema>()
    const {fields, append, remove} = useFieldArray({
        name: 'contacts',
        control: form.control
    })

    return (
        <Flexbox direction="column" gap={2} fullWidth>
            <Flexbox fullWidth justify="space-between" align="center">
                <Label>{t('commons:additional_contacts')}</Label>
                <AddUserContactDropdown isOpen={isMenuOpen} setOpen={setMenuOpen} append={append} />
            </Flexbox>

            {fields.length == 0 ? (
                <Text size="textSm" palette="neutral" shade="500">
                    {t('commons:additional_contacts_empty')}
                </Text>
            ) : (
                <Flexbox direction="column" fullWidth>
                    {fields.map((field, index) => (
                        <Flexbox key={field.id} direction="column" gap={1.5} fullWidth>
                            <Label>
                                {field.type == 'email'
                                    ? t('commons:email')
                                    : field.type == 'telephone'
                                      ? t('commons:telephone')
                                      : t('commons:mobile_phone')}
                            </Label>
                            <Flexbox gap={2.5} fullWidth align="flex-start">
                                {field.type == 'email' ? (
                                    <InputText
                                        type="email"
                                        placeholder="esempioemail@email.it"
                                        maxLength={254}
                                        data-anti-autofill="true"
                                        autoComplete="off"
                                        errorMessage={form.formState.errors.contacts?.[index]?.contact?.message}
                                        {...form.register(`contacts.${index}.contact`)}
                                    />
                                ) : (
                                    <ContactPhoneField index={index} />
                                )}
                                <InputText
                                    type="text"
                                    placeholder={t('commons:label_placeholder')}
                                    maxLength={100}
                                    errorMessage={form.formState.errors.contacts?.[index]?.label?.message}
                                    {...form.register(`contacts.${index}.label`)}
                                />
                                <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                                    <Trash01Icon />
                                </Button>
                            </Flexbox>
                            {index < fields.length - 1 && (
                                <Divider direction="horizontal" startSpacing={4} endSpacing={4} />
                            )}
                        </Flexbox>
                    ))}
                </Flexbox>
            )}
        </Flexbox>
    )
}

const AddUserContactDropdown = ({
    isOpen,
    setOpen,
    append
}: {
    isOpen: boolean
    setOpen: (value: boolean) => void
    append: (contact: {contact: string; label: string; type: Contact['type']}) => void
}) => {
    const {t} = useTranslation()
    return (
        <MenuProvider open={isOpen} setOpen={setOpen}>
            <MenuButton
                render={
                    <Button size="sm" shape="rectangle" variant="secondary">
                        {t('commons:add_contact')}
                        <ChevronDownIcon />
                    </Button>
                }
            />
            <Menu sameWidth>
                <MenuGroup>
                    <MenuItem onClick={() => append({contact: '', label: '', type: 'mobile_phone'})}>
                        {t('commons:mobile_phone')}
                    </MenuItem>
                    <MenuItem onClick={() => append({contact: '', label: '', type: 'telephone'})}>
                        {t('commons:telephone')}
                    </MenuItem>
                    <MenuItem onClick={() => append({contact: '', label: '', type: 'email'})}>
                        {t('commons:email')}
                    </MenuItem>
                </MenuGroup>
            </Menu>
        </MenuProvider>
    )
}
